import type { IScope } from "angular";
import { finalize } from "rxjs/operators";
import { cloneDeep, isEmpty, isEqual } from "lodash";
import "@progress/kendo-ui/js/kendo.autocomplete";

import type { ICompanySlim, ILabel, IResponseWithLoading, IrMap, ISite } from "App/Modules/Models";
import { InternetConnectionType, Permission, ResponsibleForConnection } from "App/Modules/Models";
import type { IPartnersRepository, ISitesRepository, ITimeZonesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";
import type { IProfileService } from "App/Modules/Auth";

export default class EditSiteInformationComponentController {
    public static $inject: string[] = [
        "$scope",
        "mmPartnersRepository",
        "mmSitesRepository",
        "mmTimeZonesRepository",
        "mmProfileService",
        "$translate",
    ];

    public originalSite: ISite;

    public site: ISite;

    public editResponse: IResponseWithLoading<ISite>;

    public companies: ICompanySlim[];

    public internetConnectionTypes: string[];

    public responsibleForConnections: string[];

    public timeZones: IrMap<string, string>;

    public partnerSiteLabels: string[];

    public siteLabels: string;

    public subscriptionTypeName: string;

    public alarmVideoLengthOptions: number[];

    public resolve: IFormContainerResolve<ISite, void>;

    constructor(
        private readonly $scope: IScope,
        private readonly partnersRepository: IPartnersRepository,
        private readonly sitesRepository: ISitesRepository,
        private readonly timeZonesRepository: ITimeZonesRepository,
        private readonly profileService: IProfileService,
        private readonly $translate: angular.translate.ITranslateService,
    ) {}

    public $onInit() {
        this.originalSite = this.resolve.formData;
        this.site = cloneDeep(this.originalSite);
        const partnerId = this.site.partner.id;

        // remove once roles and permissions have been fixed per #10599
        const hasPartnerOp = this.profileService.hasPermission(Permission.PartnerOperations);

        extendWithLoadingAndFail(this.timeZonesRepository.getAllTimeZones())
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                if (!response.hasError && response.data) {
                    this.timeZones = response.data;
                }
            });

        if (hasPartnerOp) {
            extendWithLoadingAndFail(this.partnersRepository.getPartnerCompanies(partnerId))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe((response) => {
                    if (!response.hasError && response.data) {
                        this.companies = response.data;
                    }
                });

            extendWithLoadingAndFail(this.partnersRepository.getSiteLabels(partnerId))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe((response) => {
                    if (!response.hasError && response.data) {
                        this.partnerSiteLabels = this.mapLabelsToStringArray(response.data);
                    }
                });
        }

        const internetConnectionTypes: string[] = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const n in InternetConnectionType) {
            if (typeof InternetConnectionType[n] === "number") {
                internetConnectionTypes.push(n);
            }
        }
        this.internetConnectionTypes = internetConnectionTypes;

        const responsibleForConnections: string[] = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const n in ResponsibleForConnection) {
            if (typeof ResponsibleForConnection[n] === "number") {
                responsibleForConnections.push(n);
            }
        }
        this.responsibleForConnections = responsibleForConnections;

        this.siteLabels = this.mapLabelsToStringArray(this.site.labels).join();

        this.alarmVideoLengthOptions = [7, 15];
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public editSite() {
        const patchObject = {} as ISite;

        if (this.originalSite.name !== this.site.name) {
            patchObject.name = this.site.name;
        }

        if (!isEqual(this.originalSite.company, this.site.company)) {
            if (this.site.company) {
                patchObject.company = this.site.company;
            } else {
                patchObject.company = null;
            }
        }

        if (this.originalSite.project !== this.site.project) {
            patchObject.project = this.site.project;
        }

        if (this.originalSite.timeZone !== this.site.timeZone) {
            patchObject.timeZone = this.site.timeZone;
        }

        if (!isEqual(this.originalSite.internetConnection, this.site.internetConnection)) {
            patchObject.internetConnection = this.site.internetConnection;
        }

        if (!isEqual(this.originalSite.alarmVideoLength, this.site.alarmVideoLength)) {
            patchObject.alarmVideoLength = this.site.alarmVideoLength;
        }

        const labels = this.siteLabels
            ? this.siteLabels.split(",").map((labelText) => ({ label: labelText } as ILabel))
            : [];
        if (!isEqual(this.originalSite.labels, labels)) {
            patchObject.labels = labels;
        }

        if (!isEmpty(patchObject)) {
            extendWithLoadingAndFail(this.sitesRepository.patchSite(this.originalSite.id, patchObject))
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (editResponse: IResponseWithLoading<ISite>) => {
                        this.editResponse = editResponse;
                        if (!editResponse.isLoading && editResponse.data) {
                            this.resolve.options.onOk(editResponse.data);
                        }
                    },
                    (errorResponse) => {
                        this.editResponse = errorResponse;
                    },
                );
        } else {
            this.cancel();
        }
    }

    private mapLabelsToStringArray(labels: ILabel[]): string[] {
        return labels.map((labelModel) => labelModel.label).sort();
    }
}
